import Phaser from 'phaser';
import { COLORS, GAME_W, GAME_H, intToHex, PLAYER_SPEED, PLAYER_JUMP_VEL } from '../utils/constants';
import { soundManager } from '../systems/SoundManager';
import { weaponSystem, Weapon } from '../systems/WeaponSystem';
import { walletManager } from '../systems/WalletManager';
import { NetworkManager, PlayerState } from '../systems/NetworkManager';

interface PVPPlayer {
  sprite: Phaser.Physics.Arcade.Sprite;
  hp: number;
  maxHp: number;
  name: string;
  weapon: Weapon;
  facingRight: boolean;
  lastAttackTime: number;
  isDodging: boolean;
  isInvulnerable: boolean;
  graphics: Phaser.GameObjects.Graphics;
  trailGraphics: Phaser.GameObjects.Graphics;
  combo: number;
  shieldActive: boolean;
  lastShieldTime: number;
  lastDashTime: number;
}

const PVP_ARENA_W = 1600;
const PVP_ARENA_H = 720;
const FLOOR_Y = PVP_ARENA_H - 100;
const P1_KEYS = { left: 'A', right: 'D', up: 'W', down: 'S', attack: 'J', heavy: 'K', skill: 'L', shield: 'SHIFT', dash: 'Q' };
const P2_KEYS = { left: 'LEFT', right: 'RIGHT', up: 'UP', down: 'DOWN', attack: 'NUMPAD_ONE', heavy: 'NUMPAD_TWO', skill: 'NUMPAD_THREE', shield: 'NUMPAD_ZERO', dash: 'NUMPAD_PERIOD' };

export default class PVPScene extends Phaser.Scene {
  private player1!: PVPPlayer;
  private player2!: PVPPlayer;
  private battleActive = false;
  private paused = false;
  private isOnline = false;
  private network: NetworkManager | null = null;
  private p2IsLocal = true;
  private scoreP1 = 0;
  private scoreP2 = 0;
  private roundTimer = 0;
  private maxRoundTime = 99;
  private timerText!: Phaser.GameObjects.Text;
  private p1HpBar!: Phaser.GameObjects.Graphics;
  private p2HpBar!: Phaser.GameObjects.Graphics;
  private p1NameText!: Phaser.GameObjects.Text;
  private p2NameText!: Phaser.GameObjects.Text;
  private p1ScoreText!: Phaser.GameObjects.Text;
  private p2ScoreText!: Phaser.GameObjects.Text;
  private resultText: Phaser.GameObjects.Text | null = null;
  private p1Name = '玩家1';
  private p2Name = '玩家2';
  private attackGraphics: Phaser.GameObjects.Graphics;

  constructor() { super({ key: 'PVPScene' }); }

  init(data: any) {
    this.p1Name = data?.p1Name || '玩家1';
    this.p2Name = data?.p2Name || '玩家2';
    this.p2IsLocal = data?.local !== false;
    this.isOnline = data?.online === true;
    if (this.isOnline) {
      this.network = data?.network || null;
    }
  }

  create() {
    this.physics.world.setBounds(0, 0, PVP_ARENA_W, PVP_ARENA_H);
    this.createArena();

    // 确保玩家纹理存在
    if (!this.textures.exists('pvp_p1')) {
      const g = this.add.graphics({ x: 0, y: 0 });
      g.fillStyle(COLORS.cyan, 1);
      g.fillRect(4, 0, 24, 16);
      g.fillStyle(0x111111, 1);
      g.fillRect(6, 18, 20, 34);
      g.fillStyle(COLORS.cyan, 0.8);
      g.fillRect(8, 22, 16, 6);
      g.lineStyle(1, COLORS.cyan, 0.5);
      g.strokeRect(4, 0, 24, 52);
      g.generateTexture('pvp_p1', 32, 52);
      g.destroy();
    }
    if (!this.textures.exists('pvp_p2')) {
      const g = this.add.graphics({ x: 0, y: 0 });
      g.fillStyle(COLORS.red, 1);
      g.fillRect(4, 0, 24, 16);
      g.fillStyle(0x111111, 1);
      g.fillRect(6, 18, 20, 34);
      g.fillStyle(COLORS.red, 0.8);
      g.fillRect(8, 22, 16, 6);
      g.lineStyle(1, COLORS.red, 0.5);
      g.strokeRect(4, 0, 24, 52);
      g.generateTexture('pvp_p2', 32, 52);
      g.destroy();
    }

    this.player1 = this.createPVPPlayer(300, FLOOR_Y - 30, this.p1Name, P1_KEYS, 'pvp_p1');
    this.player2 = this.createPVPPlayer(PVP_ARENA_W - 300, FLOOR_Y - 30, this.p2Name, P2_KEYS, 'pvp_p2');

    this.attackGraphics = this.add.graphics().setDepth(15);

    this.cameras.main.setBounds(0, 0, PVP_ARENA_W, PVP_ARENA_H);
    this.cameras.main.setBackgroundColor(COLORS.bgDark);

    this.p1HpBar = this.add.graphics().setDepth(50).setScrollFactor(0);
    this.p2HpBar = this.add.graphics().setDepth(50).setScrollFactor(0);
    this.p1NameText = this.add.text(30, 16, this.p1Name, {
      fontFamily: 'Microsoft YaHei, sans-serif', fontSize: '18px', color: intToHex(COLORS.cyan), fontStyle: 'bold',
    }).setDepth(51).setScrollFactor(0);
    this.p2NameText = this.add.text(GAME_W - 30, 16, this.p2Name, {
      fontFamily: 'Microsoft YaHei, sans-serif', fontSize: '18px', color: intToHex(COLORS.red), fontStyle: 'bold',
    }).setOrigin(1, 0).setDepth(51).setScrollFactor(0);
    this.p1ScoreText = this.add.text(GAME_W / 2 - 60, 16, '0', {
      fontFamily: 'Arial Black', fontSize: '32px', color: '#ffffff',
    }).setOrigin(0.5).setDepth(51).setScrollFactor(0);
    this.p2ScoreText = this.add.text(GAME_W / 2 + 60, 16, '0', {
      fontFamily: 'Arial Black', fontSize: '32px', color: '#ffffff',
    }).setOrigin(0.5).setDepth(51).setScrollFactor(0);

    const vsText = this.add.text(GAME_W / 2, 30, 'VS', {
      fontFamily: 'Arial Black', fontSize: '20px', color: '#555577',
    }).setOrigin(0.5).setDepth(51).setScrollFactor(0);

    this.timerText = this.add.text(GAME_W / 2, 56, `${this.maxRoundTime}`, {
      fontFamily: 'Arial, monospace', fontSize: '18px', color: COLORS.textDim,
    }).setOrigin(0.5).setDepth(51).setScrollFactor(0);

    this.battleActive = true;
    this.roundTimer = this.maxRoundTime;

    this.time.addEvent({
      delay: 1000, loop: true,
      callback: () => {
        if (!this.battleActive || this.paused) return;
        this.roundTimer--;
        this.timerText.setText(String(Math.max(0, this.roundTimer)));
        if (this.roundTimer <= 0) this.endRound();
      },
    });

    this.showStartAnimation();

    if (this.isOnline && this.network) {
      this.network.onMessage((data) => {
        if (data.type === 'state') {
          this.player2.sprite.setPosition(data.x, data.y);
          this.player2.hp = data.hp;
          this.player2.facingRight = data.facingRight;
          this.player2.weapon = weaponSystem.getWeaponList().find((w: Weapon) => w.id === data.weaponId) || weaponSystem.getEquipped();
        } else if (data.type === 'action') {
          if (data.type === 'damage') {
            this.applyDamage(this.player1, data.damage);
          }
        }
      });
    }

    this.events.on('shutdown', () => {
      this.player1?.graphics?.destroy();
      this.player1?.trailGraphics?.destroy();
      this.player2?.graphics?.destroy();
      this.player2?.trailGraphics?.destroy();
      this.attackGraphics?.destroy();
    });
  }

  private createPVPPlayer(x: number, y: number, name: string, keys: any, texture: string): PVPPlayer {
    const sprite = this.physics.add.sprite(x, y, texture);
    sprite.setCollideWorldBounds(true);
    sprite.setGravityY(0);
    (sprite.body as Phaser.Physics.Arcade.Body).setSize(28, 60);
    (sprite.body as Phaser.Physics.Arcade.Body).setOffset(10, 8);
    sprite.setDepth(10);

    const graphics = this.add.graphics().setDepth(11);
    const trailGraphics = this.add.graphics().setDepth(9);

    return {
      sprite, hp: 100, maxHp: 100, name,
      weapon: weaponSystem.getEquipped(),
      facingRight: name === this.p1Name,
      lastAttackTime: 0, isDodging: false, isInvulnerable: false,
      graphics, trailGraphics, combo: 0,
      shieldActive: false, lastShieldTime: 0, lastDashTime: 0,
    };
  }

  private createArena() {
    const bg = this.add.graphics().setDepth(0);
    bg.fillGradientStyle(0x050510, 0x0a0a2a, 0x0a0a1a, 0x1a0a2a);
    bg.fillRect(0, 0, PVP_ARENA_W, PVP_ARENA_H);

    const floor = this.add.graphics().setDepth(1);
    floor.fillStyle(COLORS.bg, 1);
    floor.fillRect(0, FLOOR_Y + 20, PVP_ARENA_W, PVP_ARENA_H - FLOOR_Y);
    floor.lineStyle(2, COLORS.cyan, 0.15);
    floor.lineBetween(0, FLOOR_Y, PVP_ARENA_W, FLOOR_Y);

    const centerLine = this.add.graphics().setDepth(2);
    centerLine.lineStyle(2, COLORS.cyan, 0.1);
    centerLine.lineBetween(PVP_ARENA_W / 2, 80, PVP_ARENA_W / 2, FLOOR_Y);

    const d = this.add.graphics().setDepth(1);
    d.lineStyle(1, COLORS.cyan, 0.04);
    for (let x = 0; x < PVP_ARENA_W; x += 100) d.lineBetween(x, FLOOR_Y, x, PVP_ARENA_H);
    for (let y = FLOOR_Y; y < PVP_ARENA_H; y += 100) d.lineBetween(0, y, PVP_ARENA_W, y);
  }

  private handlePVPInput(player: PVPPlayer, keys: any) {
    if (!this.battleActive || this.paused) return;
    if (player.hp <= 0 || player.isDodging) return;

    const body = player.sprite.body as Phaser.Physics.Arcade.Body;
    let moveX = 0;

    if (this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes[keys.left]).isDown) moveX = -1;
    if (this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes[keys.right]).isDown) moveX = 1;
    if (Phaser.Input.Keyboard.JustDown(this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes[keys.up]))) {
      if (body.blocked.down) body.setVelocityY(PLAYER_JUMP_VEL);
    }

    body.setVelocityX(moveX * PLAYER_SPEED);
    if (moveX !== 0) {
      player.facingRight = moveX > 0;
      player.sprite.setFlipX(moveX < 0);
    }

    if (Phaser.Input.Keyboard.JustDown(this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes[keys.down]))) {
      if (body.blocked.down) this.startPVPDodge(player);
    }

    if (Phaser.Input.Keyboard.JustDown(this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes[keys.attack]))) {
      this.performPVPAttack(player, 'normal');
    }
    if (Phaser.Input.Keyboard.JustDown(this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes[keys.heavy]))) {
      this.performPVPAttack(player, 'heavy');
    }
    if (Phaser.Input.Keyboard.JustDown(this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes[keys.skill]))) {
      this.performPVPAttack(player, 'skill');
    }

    if (Phaser.Input.Keyboard.JustDown(this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes[keys.shield]))) {
      if (!player.shieldActive && this.time.now - player.lastShieldTime > 5000) {
        this.activatePVPShield(player);
      }
    }
    if (Phaser.Input.Keyboard.JustDown(this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes[keys.dash]))) {
      if (!player.isDodging && this.time.now - player.lastDashTime > 4000) {
        this.startPVPDash(player);
      }
    }
  }

  private startPVPDodge(player: PVPPlayer) {
    player.isDodging = true;
    player.isInvulnerable = true;
    soundManager.play('dodge');
    const dir = player.facingRight ? 1 : -1;
    (player.sprite.body as Phaser.Physics.Arcade.Body).setVelocityX(dir * 600);
    player.sprite.setAlpha(0.4);
    this.time.delayedCall(300, () => {
      player.isDodging = false;
      player.isInvulnerable = false;
      player.sprite.setAlpha(1);
      (player.sprite.body as Phaser.Physics.Arcade.Body).setVelocityX(0);
    });
  }

  private startPVPDash(player: PVPPlayer) {
    player.isDodging = true;
    player.isInvulnerable = true;
    player.lastDashTime = this.time.now;
    soundManager.play('dash');
    const dir = player.facingRight ? 1 : -1;
    (player.sprite.body as Phaser.Physics.Arcade.Body).setVelocityX(dir * 700);
    player.sprite.setAlpha(0.6);
    this.time.delayedCall(200, () => {
      player.isDodging = false;
      player.isInvulnerable = false;
      player.sprite.setAlpha(1);
      (player.sprite.body as Phaser.Physics.Arcade.Body).setVelocityX(0);
    });
  }

  private activatePVPShield(player: PVPPlayer) {
    player.shieldActive = true;
    player.lastShieldTime = this.time.now;
    soundManager.play('shield');
    this.time.delayedCall(2000, () => { player.shieldActive = false; });
  }

  private performPVPAttack(player: PVPPlayer, type: 'normal' | 'heavy' | 'skill') {
    const now = this.time.now;
    const cd = type === 'heavy' ? 1200 : type === 'skill' ? 2000 : 350;
    if (now - player.lastAttackTime < cd) return;
    player.lastAttackTime = now;

    const weapon = player.weapon;
    const dmgMulti = type === 'heavy' ? 1.5 : type === 'skill' ? 2.0 : 1.0;
    const damage = Math.floor(weapon.damage * dmgMulti);
    const dir = player.facingRight ? 1 : -1;
    const ox = dir * 30;
    const size = type === 'skill' ? 40 : type === 'heavy' ? 36 : 28;

    this.attackGraphics.fillStyle(weapon.color, 0.4);
    this.attackGraphics.fillCircle(player.sprite.x + ox, player.sprite.y - 5, size);
    this.time.delayedCall(150, () => this.attackGraphics.clear());

    const other = player === this.player1 ? this.player2 : this.player1;
    const dist = Phaser.Math.Distance.Between(
      player.sprite.x + ox, player.sprite.y - 5,
      other.sprite.x, other.sprite.y
    );
    if (dist < weapon.range + 20 && !other.isInvulnerable && other.hp > 0) {
      this.applyDamage(other, damage);
      player.combo++;
      this.floatingText(other.sprite.x, other.sprite.y - 40, `-${damage}`, '#ff1744');
      soundManager.play('boss_hit');
    } else {
      player.combo = 0;
    }

    if (player === this.player1 && this.isOnline && this.network) {
      this.network.sendAction({ type: 'damage', attacker: 1, damage });
    }
  }

  private applyDamage(player: PVPPlayer, amount: number) {
    if (player.isInvulnerable || player.shieldActive || player.hp <= 0) return;
    if (player.shieldActive) {
      player.shieldActive = false;
      return;
    }
    player.hp = Math.max(0, player.hp - amount);
    player.isInvulnerable = true;
    player.sprite.setTint(0xff0000);
    this.time.delayedCall(100, () => { if (player.sprite.active) player.sprite.clearTint(); });
    this.time.delayedCall(500, () => { player.isInvulnerable = false; });
    if (player.hp <= 0) this.endRound();
  }

  private endRound() {
    if (!this.battleActive) return;
    this.battleActive = false;

    let winner = 0;
    if (this.player1.hp <= 0 && this.player2.hp <= 0) winner = 0;
    else if (this.player2.hp <= 0 || this.player1.hp > this.player2.hp) { winner = 1; this.scoreP1++; }
    else if (this.player1.hp <= 0 || this.player2.hp > this.player1.hp) { winner = 2; this.scoreP2++; }
    else winner = 0;

    this.p1ScoreText.setText(String(this.scoreP1));
    this.p2ScoreText.setText(String(this.scoreP2));

    const winText = winner === 0 ? '平局' : winner === 1 ? `${this.p1Name} 获胜!` : `${this.p2Name} 获胜!`;
    this.resultText = this.add.text(GAME_W / 2, GAME_H / 2 - 20, winText, {
      fontFamily: 'Microsoft YaHei, Arial, sans-serif', fontSize: '48px',
      color: winner === 1 ? intToHex(COLORS.cyan) : winner === 2 ? intToHex(COLORS.red) : '#ffffff',
      fontStyle: 'bold', stroke: '#000000', strokeThickness: 4,
    }).setOrigin(0.5).setDepth(60).setAlpha(0);
    this.tweens.add({ targets: this.resultText, alpha: 1, scaleX: 1.2, scaleY: 1.2, duration: 400, ease: 'Back.easeOut' });

    this.time.delayedCall(2000, () => {
      this.resetRound();
      this.battleActive = true;
      if (this.resultText) { this.resultText.destroy(); this.resultText = null; }
    });
  }

  private resetRound() {
    this.player1.hp = this.player1.maxHp;
    this.player2.hp = this.player2.maxHp;
    this.player1.sprite.setPosition(300, FLOOR_Y - 30);
    this.player2.sprite.setPosition(PVP_ARENA_W - 300, FLOOR_Y - 30);
    this.roundTimer = this.maxRoundTime;
    this.timerText.setText(String(this.maxRoundTime));
  }

  private floatingText(x: number, y: number, text: string, color = '#00f0ff') {
    const ft = this.add.text(x, y - 20, text, {
      fontFamily: 'Arial', fontSize: '16px', color, fontStyle: 'bold',
      stroke: '#000000', strokeThickness: 2,
    }).setOrigin(0.5).setDepth(55);
    this.tweens.add({ targets: ft, y: y - 60, alpha: 0, duration: 800, onComplete: () => ft.destroy() });
  }

  private drawPVPHP(player: PVPPlayer, hpBar: Phaser.GameObjects.Graphics, isRight: boolean) {
    hpBar.clear();
    const barW = 300;
    const barH = 18;
    const x = isRight ? GAME_W - 30 - barW : 30;
    const y = 42;
    const hp = player.hp / player.maxHp;

    hpBar.fillStyle(0x000000, 0.7);
    hpBar.fillRoundedRect(x - 1, y - 1, barW + 2, barH + 2, 4);
    hpBar.fillStyle(COLORS.darkGray, 1);
    hpBar.fillRoundedRect(x, y, barW, barH, 3);

    const col = hp > 0.6 ? COLORS.green : hp > 0.3 ? COLORS.yellow : COLORS.red;
    if (hp > 0.01) {
      const fillW = barW * hp;
      const actualX = isRight ? x + barW - fillW : x;
      hpBar.fillStyle(col, 1);
      hpBar.fillRoundedRect(actualX, y + 2, fillW, barH - 4, 2);
      hpBar.fillStyle(0xffffff, 0.15);
      hpBar.fillRect(actualX, y + 2, fillW, 4);
    }

    const label = isRight ? this.p2NameText : this.p1NameText;
    label.setPosition(isRight ? GAME_W - 30 : 30, y - 12);
  }

  private showStartAnimation() {
    const cx = GAME_W / 2;
    const cy = GAME_H / 2;
    const text = this.add.text(cx, cy, 'FIGHT!', {
      fontFamily: 'Arial Black, sans-serif', fontSize: '72px', color: '#ffea00',
      stroke: '#000000', strokeThickness: 5,
    }).setOrigin(0.5).setDepth(60).setAlpha(0).setScrollFactor(0);
    this.tweens.add({
      targets: text, alpha: 1, scaleX: 1.5, scaleY: 1.5, duration: 400,
      yoyo: true, hold: 500, onComplete: () => text.destroy(),
    });
  }

  update(time: number, delta: number) {
    if (!this.battleActive) return;
    this.handlePVPInput(this.player1, P1_KEYS);
    if (this.p2IsLocal && !this.isOnline) {
      this.handlePVPInput(this.player2, P2_KEYS);
    }

    this.drawPVPHP(this.player1, this.p1HpBar, false);
    this.drawPVPHP(this.player2, this.p2HpBar, true);

    if (this.isOnline && this.network && this.network.isConnected()) {
      this.network.sendState({
        x: this.player1.sprite.x, y: this.player1.sprite.y,
        hp: this.player1.hp, maxHp: this.player1.maxHp,
        facingRight: this.player1.facingRight,
        attacking: false, attackType: null,
        shieldActive: this.player1.shieldActive,
        weaponId: this.player1.weapon.id,
        playerName: this.p1Name,
      });
    }

    this.player1.trailGraphics.clear();
    this.player2.trailGraphics.clear();
    if (this.player1.isDodging) {
      this.player1.trailGraphics.fillStyle(COLORS.cyan, 0.15);
      this.player1.trailGraphics.fillRect(this.player1.sprite.x - 18, this.player1.sprite.y - 30, 36, 60);
    }
    if (this.player2.isDodging) {
      this.player2.trailGraphics.fillStyle(COLORS.red, 0.15);
      this.player2.trailGraphics.fillRect(this.player2.sprite.x - 18, this.player2.sprite.y - 30, 36, 60);
    }
  }
}
